import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged, fromEvent } from 'rxjs';
import { Router } from '@angular/router';
import { OrdersService } from '../../../../core/services/orders/orders.service';

@Component({
  selector: 'app-search-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './search-overlay.component.html',
  styleUrl: './search-overlay.component.css'
})
export class SearchOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @Output() closeOverlay = new EventEmitter<void>();

  searchText = '';
  searchSuggestions: any[] = [];
  isSearching = false;
  hasSearched = false;
  errorMessage = '';

  private searchTerms = new Subject<string>();
  private isSearchTriggeredByEnter = false;
  private subscriptions = new Subscription();
  private popStateHandler = () => this.close();

  constructor(private orderService: OrdersService, private router: Router) {}

  ngOnInit(): void {
    // Push state for back button handling
    history.pushState({ searchOverlay: true }, '');
    window.addEventListener('popstate', this.popStateHandler);

    // Debounce search
    this.subscriptions.add(
      this.searchTerms
        .pipe(debounceTime(1000), distinctUntilChanged())
        .subscribe(() => {
          if (!this.isSearchTriggeredByEnter) {
            if (this.searchText.trim() !== '') {
              this.searchPro();
            } else {
              this.clearResults();
            }
          }
          this.isSearchTriggeredByEnter = false;
        })
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.searchInput?.nativeElement.focus(), 100);

    // Keyup events
    this.subscriptions.add(
      fromEvent<KeyboardEvent>(this.searchInput.nativeElement, 'keyup')
        .subscribe((event) => {
          this.searchTerms.next(this.searchText);
          if (event.key === 'Enter' && this.searchText.trim() !== '') {
            this.isSearchTriggeredByEnter = true;
            this.searchPro();
          }
        })
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.popStateHandler);
    this.subscriptions.unsubscribe();
  }

  searchPro(): void {
    this.isSearching = true;
    this.errorMessage = '';
    this.orderService.searchPro(this.searchText).subscribe({
      next: (data) => {
        this.searchSuggestions = [data.data];
        this.hasSearched = true;
        this.isSearching = false;
      },
      error: () => {
        this.errorMessage = 'error';
        this.isSearching = false;
        this.hasSearched = true;
      }
    });
  }

  onResultClick(link: string): void {
    this.close();
    // Use Angular Router for SPA navigation instead of full page reload
    try {
      const url = new URL(link, window.location.origin);
      this.router.navigateByUrl(url.pathname);
    } catch {
      this.router.navigateByUrl(link);
    }
  }

  clearSearch(): void {
    this.searchText = '';
    this.clearResults();
    this.searchInput?.nativeElement.focus();
  }

  close(): void {
    this.searchText = '';
    this.clearResults();
    this.closeOverlay.emit();
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  get hasResults(): boolean {
    if (!this.searchSuggestions.length) return false;
    return this.searchSuggestions.some(group =>
      this.objectKeys(group).some(key => group[key]?.items?.length > 0)
    );
  }

  private clearResults(): void {
    this.searchSuggestions = [];
    this.hasSearched = false;
    this.errorMessage = '';
  }
}
